import React, { useEffect, useState, useRef, useCallback } from 'react'
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { classNames } from '../../utils/classNames'
import { ToastNotification } from '../../types/notification'

interface ToastProps {
  toast: ToastNotification
  onRemove: (id: string) => void
  onAction?: () => void
}

/** Safe remove: only call onRemove if component is still mounted; clears pending timeouts on unmount */
function useSafeRemove(onRemove: (id: string) => void, toastId: string) {
  const isMountedRef = useRef(true)
  const onRemoveRef = useRef(onRemove)
  onRemoveRef.current = onRemove
  useEffect(() => { isMountedRef.current = true; return () => { isMountedRef.current = false } }, [])
  return useCallback(() => {
    if (isMountedRef.current) onRemoveRef.current(toastId)
  }, [toastId])
}

const iconMap = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
}

const colorMap = {
  success: {
    bg: 'bg-success-50',
    border: 'border-success-200',
    text: 'text-success-800',
    icon: 'text-success-400',
  },
  error: {
    bg: 'bg-danger-50',
    border: 'border-danger-200',
    text: 'text-danger-800',
    icon: 'text-danger-400',
  },
  warning: {
    bg: 'bg-warning-50',
    border: 'border-warning-200',
    text: 'text-warning-800',
    icon: 'text-warning-400',
  },
  info: {
    bg: 'bg-info-50',
    border: 'border-info-200',
    text: 'text-info-800',
    icon: 'text-info-400',
  },
}

export const Toast: React.FC<ToastProps> = ({ toast, onRemove, onAction }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [progress, setProgress] = useState(100)
  const safeRemove = useSafeRemove(onRemove, toast.id)
  const pendingTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])

  const IconComponent = iconMap[toast.type]
  const colors = colorMap[toast.type]
  const duration = toast.duration || 5000

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100)

    // Auto-remove timer (store inner timeout for cleanup)
    const removeTimer = setTimeout(() => {
      setIsVisible(false)
      const innerId = setTimeout(() => safeRemove(), 300)
      pendingTimeoutsRef.current.push(innerId)
    }, duration)

    // Progress bar
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev - (100 / (duration / 100))
        return newProgress <= 0 ? 0 : newProgress
      })
    }, 100)

    return () => {
      clearTimeout(timer)
      clearTimeout(removeTimer)
      clearInterval(progressInterval)
      pendingTimeoutsRef.current.forEach(clearTimeout)
      pendingTimeoutsRef.current = []
    }
  }, [toast.id, duration, safeRemove])

  const handleRemove = useCallback(() => {
    setIsVisible(false)
    const id = setTimeout(() => safeRemove(), 300)
    pendingTimeoutsRef.current.push(id)
  }, [safeRemove])

  return (
    <div
      className={`
        transform transition-all duration-300 ease-in-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className={`
        relative overflow-hidden rounded-lg border p-4 shadow-lg
        ${colors.bg} ${colors.border}
      `}>
        {/* Progress bar */}
        <div className="absolute top-0 left-0 h-1 bg-current opacity-20 transition-all duration-100"
             style={{ width: `${progress}%` }} />
        
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <IconComponent className={`h-5 w-5 ${colors.icon}`} />
          </div>
          
          <div className="ml-3 flex-1">
            <p className={`text-sm font-medium ${colors.text}`}>
              {toast.title}
            </p>
            {toast.message && (
              <p className={`mt-1 text-sm ${colors.text} opacity-80`}>
                {toast.message}
              </p>
            )}
            {toast.action && (
              <button
                onClick={() => {
                  toast.action?.onClick()
                  handleRemove()
                }}
                className={`mt-2 text-sm font-medium underline ${colors.text} hover:opacity-80`}
              >
                {toast.action.label}
              </button>
            )}
          </div>
          
          <div className="ml-4 flex flex-shrink-0">
            <button
              onClick={handleRemove}
              className={`
                inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2
                ${colors.text} hover:opacity-80 focus:ring-current
              `}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
